/**
 * Client error reporting → POST /api/client-errors (listed in Stjórnun → Villuskráning).
 *
 * Captures uncaught errors, unhandled promise rejections, Vite chunk-preload failures and render
 * errors (route boundaries and the app-level boundary call reportClientError). Reports are kept in
 * memory, deduplicated per session and capped, and sent in batches of at most 10 — only while a
 * user is signed in and the device is online — on a timer, when the app goes to the background
 * and when the connection comes back.
 *
 * Privacy: only the error name, a trimmed message with a few stack frames and the route pathname
 * are sent. Invite/reset tokens, query strings, URL fragments and e-mail addresses are redacted;
 * nothing from forms is ever read.
 *
 * Installed by a side-effect import from App.tsx.
 */
import type { MeDTO } from '../shared/dto/auth';
import type { ClientErrorReport, ClientErrorsRequest } from '../shared/dto/members';
import { ApiFailure, NetworkFailure } from './api/client';
import { queryClient } from './api/queryClient';

/** Server caps (src/server/routes/system.ts): 10 rows per request, message ≤ 2000, route ≤ 200, code ≤ 100. */
export const BATCH_SIZE = 10;
export const MAX_MESSAGE = 500;
export const MAX_ROUTE = 200;
export const MAX_CODE = 100;
/** Distinct reports accepted per page session (the server also limits to 30 per user per minute). */
export const MAX_PER_SESSION = 30;
/** A queued batch is retried once after a network failure, then dropped. */
export const MAX_TRIES = 2;
export const FLUSH_DELAY_MS = 30_000;
const MAX_FRAMES = 3;

/**
 * A lazy route chunk (or its CSS) could not be loaded: offline, or a deploy replaced the hashes.
 * Safari words a stale chunk URL (answered with the app's index.html) as "'text/html' is not a valid
 * JavaScript MIME type".
 */
const CHUNK_RE = /dynamically imported module|Importing a module script failed|module script|Unable to preload CSS|is not a valid JavaScript MIME type/i;
/** Browser noise that says nothing about the app. */
const NOISE_RE = /^(ResizeObserver loop|Script error\.?$)/i;

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') return (err as { message: string }).message;
  return '';
}

export function isChunkLoadError(err: unknown): boolean {
  if (err && typeof err === 'object' && (err as { name?: unknown }).name === 'ChunkLoadError') return true;
  return CHUNK_RE.test(messageOf(err));
}

/** Errors that are expected outcomes (API answers, offline, aborts) rather than client defects. */
export function isIgnorable(err: unknown): boolean {
  if (err === undefined || err === null) return true;
  if (err instanceof NetworkFailure || err instanceof ApiFailure) return true;
  if (err && typeof err === 'object' && (err as { name?: unknown }).name === 'AbortError') return true;
  const msg = messageOf(err).trim();
  return NOISE_RE.test(msg);
}

/** Removes secrets and personal data from free text (messages, stack frames, routes). */
export function redact(text: string): string {
  return (
    text
      // Invite / password-reset tokens (/#/virkja/<token>) and invite links by voice (/#/skraning/<token>).
      .replace(/virkja\/[A-Za-z0-9_-]+/g, 'virkja/…')
      .replace(/skraning\/[A-Za-z0-9_-]+/g, 'skraning/…')
      // Query strings and fragments of URLs (search text, tokens, anchors).
      .replace(/((?:https?|blob):\/\/[^\s?#'"()<>]*)[?#][^\s'"()<>]*/gi, '$1')
      // Bare hash routes ("#/…"); plain anchors and codes ("#spjall", "React error #418") stay.
      .replace(/(^|[\s'"(])#\/[^\s'"()<>]*/g, '$1#/…')
      // key=value / key: value secrets.
      .replace(/\b(token|password|passwd|secret|api[_-]?key|session)(\s*["']?\s*[:=]\s*["']?)[^\s"',;&)]+/gi, '$1$2…')
      // E-mail addresses.
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>')
  );
}

function stackFrames(err: Error): string[] {
  const out: string[] = [];
  for (const raw of (err.stack ?? '').split('\n')) {
    const line = raw.trim();
    // Chrome: "at fn (url:1:2)"; Safari/Firefox: "fn@url:1:2". The first Chrome line repeats the message.
    if (/^at\s/.test(line) || /@\S+:\d+/.test(line)) out.push(line.length > 160 ? `${line.slice(0, 159)}…` : line);
    if (out.length >= MAX_FRAMES) break;
  }
  return out;
}

// Replaced by Vite (vite.config.ts `define`); not defined under Vitest. Declared here as well so the
// module also typechecks in the test project (which has no vite-env.d.ts).
declare const __APP_VERSION__: string | undefined;
const appVersion = (): string => (typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev');

const cap = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/** Builds the row sent to the server (redacted and capped). */
export function toReport(err: unknown, ctx: { route?: string; kind?: string } = {}): ClientErrorReport {
  let code: string;
  let body: string;
  if (err instanceof Error) {
    code = err.name || ctx.kind || 'Error';
    body = [err.message || '(no message)', ...stackFrames(err)].join('\n');
  } else if (typeof err === 'string') {
    code = ctx.kind ?? 'error';
    body = err;
  } else {
    // Never serialise arbitrary objects: they may hold form contents or personal data.
    code = ctx.kind ?? 'error';
    const name = err && typeof err === 'object' ? (err.constructor?.name ?? 'Object') : typeof err;
    body = messageOf(err) || `Non-Error value (${name})`;
  }
  // The kind (where it was caught) goes with the version: an Error's code is its name, which alone
  // does not tell e.g. an unhandled rejection from a failed annotation write.
  const tag = err instanceof Error && ctx.kind ? ` ${ctx.kind}` : '';
  const message = cap(redact(`${body}\n[v${appVersion()}${tag}]`), MAX_MESSAGE);
  const route = ctx.route === undefined ? undefined : cap(redact(ctx.route), MAX_ROUTE);
  return { message, code: cap(code, MAX_CODE), ...(route ? { route } : {}) };
}

export type SendResult = 'ok' | 'drop' | 'retry';

export interface ReporterDeps {
  send: (errors: ClientErrorReport[]) => Promise<SendResult>;
  /** Signed in (the endpoint needs a session). */
  signedIn: () => boolean;
  online: () => boolean;
  /** Current route pathname (no search or hash). */
  route: () => string;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

interface Queued {
  report: ClientErrorReport;
  tries: number;
}

export interface ErrorReporter {
  /** Queues an error; returns false when it was ignored, a duplicate or over the session cap. */
  report: (err: unknown, ctx?: { route?: string; kind?: string }) => boolean;
  /** Sends what is queued (when signed in and online). Resolves when done; never rejects. */
  flush: () => Promise<void>;
  pending: () => number;
}

export function createErrorReporter(deps: ReporterDeps): ErrorReporter {
  const seen = new Set<string>();
  let accepted = 0;
  let queue: Queued[] = [];
  let timer: unknown = null;
  let inFlight: Promise<void> | null = null;

  const schedule = () => {
    if (timer !== null || queue.length === 0) return;
    timer = deps.setTimer(() => {
      timer = null;
      void flush();
    }, FLUSH_DELAY_MS);
  };

  const report: ErrorReporter['report'] = (err, ctx = {}) => {
    if (isIgnorable(err)) return false;
    // Offline chunk failures are expected (admin chunks are not precached) and already explained on screen.
    if (isChunkLoadError(err) && !deps.online()) return false;
    const r = toReport(err, { ...ctx, route: ctx.route ?? deps.route() });
    // Same error = same name, message (first line, without the stack) and route.
    const key = `${r.code}\u0000${r.message.split('\n', 1)[0]}\u0000${r.route ?? ''}`;
    if (seen.has(key) || accepted >= MAX_PER_SESSION) return false;
    seen.add(key);
    accepted++;
    queue.push({ report: r, tries: 0 });
    schedule();
    return true;
  };

  const run = async () => {
    while (queue.length > 0 && deps.signedIn() && deps.online()) {
      const batch = queue.slice(0, BATCH_SIZE);
      let res: SendResult;
      try {
        res = await deps.send(batch.map((q) => q.report));
      } catch {
        res = 'retry';
      }
      if (res === 'retry') {
        for (const q of batch) q.tries++;
        queue = queue.filter((q) => q.tries < MAX_TRIES);
        return;
      }
      // 'ok' or 'drop' (4xx: signed out, invalid, too large): the batch is done either way.
      // New reports are only ever appended, so the batch is still at the front.
      queue = queue.slice(batch.length);
    }
  };

  const flush = (): Promise<void> => {
    if (inFlight) return inFlight;
    if (timer !== null) {
      deps.clearTimer(timer);
      timer = null;
    }
    inFlight = run().finally(() => {
      inFlight = null;
      schedule(); // anything left (offline, signed out, retry) is tried again later
    });
    return inFlight;
  };

  return { report, flush, pending: () => queue.length };
}

/** Default transport: keepalive so a flush on "app hidden" survives the app being suspended. */
export async function sendClientErrors(errors: ClientErrorReport[]): Promise<SendResult> {
  const body: ClientErrorsRequest = { errors };
  let res: Response;
  try {
    res = await fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15_000),
    });
  } catch {
    return 'retry';
  }
  // Not the API (offline fallback page, Pages "fail open"): treat as a network failure.
  if (res.headers.get('x-kgr') !== '1') return 'retry';
  if (res.ok) return 'ok';
  return res.status >= 400 && res.status < 500 ? 'drop' : 'retry';
}

let reporter: ErrorReporter | null = null;

function browserReporter(): ErrorReporter {
  reporter ??= createErrorReporter({
    send: sendClientErrors,
    signedIn: () => !!queryClient.getQueryData<MeDTO | null>(['me']),
    online: () => navigator.onLine,
    route: () => window.location.pathname,
    setTimer: (fn, ms) => window.setTimeout(fn, ms),
    clearTimer: (h) => window.clearTimeout(h as number),
  });
  return reporter;
}

/** Report an error caught by the app (error boundaries, route errors). No-op outside the browser. */
export function reportClientError(err: unknown, ctx?: { route?: string; kind?: string }): void {
  if (typeof window === 'undefined') return;
  try {
    browserReporter().report(err, ctx);
  } catch {
    /* reporting must never throw */
  }
}

const INSTALLED = '__kgrErrorReporter';

export function installErrorReporter(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const w = window as unknown as Record<string, unknown>;
  if (w[INSTALLED]) return;
  w[INSTALLED] = true;
  const r = browserReporter();
  window.addEventListener('error', (ev) => {
    // Resource load failures (img/script/link) are plain Events without an error; they don't bubble
    // to window anyway, but never report them.
    if (!(ev instanceof ErrorEvent)) return;
    r.report(ev.error ?? ev.message, { kind: 'error' });
  });
  window.addEventListener('unhandledrejection', (ev) => r.report(ev.reason, { kind: 'unhandledrejection' }));
  // Vite's preload helper (CSS / modulepreload of a lazy chunk). Not prevented: the import still
  // rejects and the route boundary shows the "needs a connection / new version" screen.
  window.addEventListener('vite:preloadError', (ev) => r.report(ev.payload, { kind: 'preload' }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void r.flush();
  });
  window.addEventListener('online', () => void r.flush());
}

installErrorReporter();
